import debounce from "lodash/debounce";
import max from "lodash/max";
import min from "lodash/min";
import smoothscroll from "smoothscroll-polyfill";
import { autorun, observable, reaction, runInAction } from "mobx";
import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  format,
  isToday,
  isAfter,
  isBefore,
  isSameDay,
  startOfDay,
} from "date-fns";
import { heroState, liveTypes } from "./event-hero";
import Glider from "glider-js";

// SAFARI doesn't support smooth scrolling (needed in tv-guide schedule)
smoothscroll.polyfill();

jQuery(function ($) {
  // escape hatch
  if (!document.getElementById("schedule")) return;

  const initialMode =
    WP.schedule.defaultMode === "scroll"
      ? WP.schedule.horizontalMode
      : WP.schedule.defaultMode || "scroll";

  // setup mobx state
  let state = observable({
    mode: initialMode,
    horizontalMode: WP.schedule.horizontalMode,
    userChosenMode: null,
    pagination:
      WP.schedule.horizontalMode === "grid"
        ? "paginated"
        : WP.schedule.pagination || "paginated",
    activeDay: null,
    activeItem: null,
    rooms: !!WP.schedule.rooms
      ? WP.schedule.rooms.map((room) => ({
          ...room,
          isCurrent: room.id === parseInt(WP.pageID),
        }))
      : null,
    scheduleItems: WP.schedule.scheduleItems.map((item) => {
      return {
        image: item.thumbnail,
        guid: item.roomId ? `${item.roomId}_${item.guid}` : item.guid,
        roomId: item.roomId || null,
        title: item.name,
        startDateTime: getDateFromPHPUnix(item.timestamp),
        endDateTime: getDateFromPHPUnix(item.end_timestamp),
        dayOfYear: startOfDay(getDateFromPHPUnix(item.timestamp)).getTime(), //? uses 13 digit unix timestamp, not date object, as this simplifies many comparissons throughout the widget
        calLink: item.calendar_link,
        presenters: item.presenters,
        tags: item.tags,
        excerpt: item.excerpt,
      };
    }),
    get days() {
      return [...new Set(this.scheduleItems.map((item) => item.dayOfYear))];
    },
    // special items for tv-guide style grids,
    // *number of columns to kill from front of grid(per day)
    get gridOffset() {
      const firstUsedGridCol = min(
        this.scheduleItems
          .filter((item) => item.dayOfYear === this.activeDay)
          .map((item) => placeOnGrid(item.startDateTime))
      );
      return firstUsedGridCol - 1;
    },
    get lastCol() {
      return max(
        state.scheduleItems
          .filter((item) => item.dayOfYear === state.activeDay)
          .map((item) => placeOnGrid(item.endDateTime))
      );
    },
  });
  // if multi-day pagination set initial active day
  if (state.pagination === "paginated") {
    const now = new Date().getTime();
    let targetIndex = 0;
    for (let day of state.days) {
      if (day >= now) break;
      targetIndex = state.days.indexOf(day);
    }
    state.activeDay = state.days[targetIndex];
  }
  // if featured mode set initial active session
  if (WP.schedule.detailsMode === "feature") {
    let items = state.scheduleItems;
    if (state.pagination === "paginated")
      items = items.filter((item) => item.dayOfYear === state.activeDay);
    state.activeItem = items[0].guid;
    document.querySelector(
      `.schedule-event-details--featured[data-guid="${state.activeItem}"]`
    ).style.display = "flex";
  }

  // mode control - toggle
  const gridToggle = document.querySelector(".js-schedule-grid-toggle");
  gridToggle.addEventListener("click", () => {
    const newMode =
      state.mode === state.horizontalMode ? "list" : state.horizontalMode;
    runInAction(() => {
      state.mode = newMode;
      state.userChosenMode = newMode;
    });
  });
  // mode control - responsive
  modeMediaQuery();
  window.addEventListener("resize", debounce(modeMediaQuery, 100));

  // render our dynamic components
  // ? (their listeners are self-contained)
  const uiTarget = document.querySelector(
    ".event-section-schedule .event-section-content"
  );
  // ? if multi-day pagination render DayPicker
  if (state.pagination === "paginated") {
    uiTarget.appendChild(DayPicker());
  }
  uiTarget.appendChild(Schedule());

  // define components
  function DayPicker() {
    // dom el
    const wrapper = document.createElement("div");
    wrapper.classList.add("day-picker");

    autorun(() => {
      // bail when only one day
      if (state.days.length <= 1) return;

      // methods
      const setActiveDay = (e) =>
        runInAction(() => {
          const newDay = parseInt(e.target.dataset.day);
          state.activeDay = newDay;
          if (WP.schedule.detailsMode === "feature") {
            const newItemset = state.scheduleItems.filter(
              (item) => item.dayOfYear === newDay
            );
            state.activeItem = newItemset[0].guid || null;
          }
        });

      // auto template
      const { activeDay, days } = state;
      const markup = days
        .map((day) => {
          const isActive = day === activeDay,
            dayIsToday = isToday(day),
            formatted = dayIsToday ? "Today" : format(day, "iii, MMM d");
          return `
              <span
                class="day-picker__item ${
                  isActive ? "day-picker__item--active" : ""
                }"
                data-day="${day}"
              >
                ${formatted}
              </span>
            `;
        })
        .join("");
      wrapper.innerHTML = markup;

      // listeners
      wrapper
        .querySelectorAll(".day-picker__item")
        .forEach((el) => el.addEventListener("click", setActiveDay));
    });

    // output
    return wrapper;
  }

  function Schedule() {
    const wrapper = document.createElement("div");
    wrapper.classList.add("schedule");
    autorun(() => {
      const { activeDay, mode } = state;
      const rooms =
        Array.isArray(state.rooms) && state.rooms.length > 1
          ? state.rooms
          : null;

      // reset
      fadeOut(wrapper);
      setTimeout(() => {
        wrapper.classList.remove(
          "schedule--scroll",
          "schedule--list",
          "schedule--grid",
          "schedule--featured"
        );
        wrapper.classList.add(
          `schedule--${
            WP.schedule.detailsMode === "feature" ? "featured" : mode
          }`
        );
        wrapper.innerHTML =
          (mode === "scroll" && !rooms) || mode === "grid"
            ? '<span class="slider-overlay"></span>'
            : "";

        // render
        if (rooms) {
          // multiroom
          if (mode === "grid") {
            // grid mode
            wrapper.appendChild(TVGuideRooms({ rooms }));
            wrapper.appendChild(TVGuide({ day: activeDay, rooms, mode }));
          } else {
            // scroll and list modes
            if (rooms.some((room) => room.isCurrent)) {
              wrapper.appendChild(
                RoomContainer({
                  day: activeDay,
                  room: rooms.find((room) => room.isCurrent),
                  mode,
                })
              );
            }
            rooms
              .filter((room) => !room.isCurrent)
              .forEach((room) =>
                wrapper.appendChild(
                  RoomContainer({ day: activeDay, room, mode })
                )
              );
          }
        } else {
          // singleroom
          if (mode === "grid") {
            // grid mode
            wrapper.appendChild(TVGuide({ day: activeDay, rooms, mode }));
          } else {
            // scroll and list modes
            wrapper.appendChild(DaySchedule({ day: activeDay, mode }));
          }
        }

        // handle slider
        setSliderState(mode, rooms);

        // refresh "add to calendar" links (safely)
        const refreshCalLinks = setInterval(() => {
          if (typeof addeventatc !== "undefined") {
            addeventatc.refresh();
            clearInterval(refreshCalLinks);
          }
        }, 100);

        // Check that "add to calendar" script has loaded successfully
        if (typeof addeventatc == "undefined") {
          // If there was an error, hide the "add to calendar" markup
          wrapper.querySelectorAll(".addeventatc").forEach((el) => {
            el.style.display = "none";
          });
        }

        // hide grid-specific scroll buttons after render, if not on grid mode
        if (WP.schedule.horizontalMode === "grid") {
          requestAnimationFrame(() => {
            document
              .querySelectorAll(".schedule-nav .scroll-arrow")
              .forEach((arrow) => {
                arrow.style.display = mode === "grid" ? "inline" : "none";
              });
          });
        }

        // finally, fade in
        fadeIn(wrapper);
      }, 150);
    });

    return wrapper;
  }

  function TVGuideTimeScale() {
    const wrapper = document.createElement("div");
    wrapper.classList.add("tv-guide__time-scale");
    wrapper.style.gridTemplateColumns = `repeat(96, [grid-line] ${WP.schedule.blockSize}px)`;

    const earliest = state.gridOffset;
    const latest = state.lastCol;

    for (let col = earliest; col < latest; col++) {
      const h = Math.floor(col / 4);
      const m = (col % 4) * 15;
      let time = new Date();
      time.setHours(h, m, 0, 0);

      if ([15, 45].includes(time.getMinutes()))
        wrapper.innerHTML += `<span></span>`;
      else
        wrapper.innerHTML += `
            <span>
              <span class="tv-guide__time-scale-separator">|</span>
              ${format(time, "h:mm aa")}
            </span>
          `;
    }

    return wrapper;
  }

  function TVGuideRooms({ rooms }) {
    const wrapper = document.createElement("div");
    wrapper.classList.add("tv-guide__room-list");

    if (rooms.some((room) => room.isCurrent)) {
      wrapper.innerHTML = RoomLink({
        room: rooms.find((room) => room.isCurrent),
      });
    }
    rooms
      .filter((room) => !room.isCurrent)
      .forEach((room) => (wrapper.innerHTML += RoomLink({ room })));

    return wrapper;
  }

  function TVGuide({ day, rooms, mode }) {
    const wrapper = document.createElement("div");
    wrapper.classList.add("tv-guide");

    // Methods
    const sizeRoomLinks = () => {
      document
        .querySelectorAll(".schedule-room__link")
        .forEach((roomLink, i) => {
          const grid = document.querySelectorAll(".schedule__day--grid")[i];
          if (roomLink.offsetHeight > grid.offsetHeight) {
            grid.style.minHeight = roomLink.offsetHeight + "px";
          } else {
            roomLink.style.minHeight = grid.offsetHeight + "px";
          }
        });
    };

    const detectOverflowingItems = () => {
      wrapper.querySelectorAll(".schedule-item").forEach((item) => {
        if (item.scrollWidth > item.offsetWidth)
          item.classList.add("schedule-item--overflowing");
      });
    };

    const positionPlayhead = () => {
      // setup
      const playhead = wrapper.querySelector(".tv-guide__playhead"),
        now = new Date(),
        currentCol = placeOnGrid(now) - 1,
        offsetCol = currentCol - state.gridOffset,
        gridGap = 14,
        leftPad = 12,
        minOffset =
          now.getMinutes() > 52
            ? now.getMinutes() - 60
            : now.getMinutes() - (currentCol % 4) * 15,
        pxOffset = (minOffset / 15) * WP.schedule.blockSize,
        pxDist =
          leftPad +
          offsetCol * (parseInt(WP.schedule.blockSize) + gridGap) +
          pxOffset;
      // bail if now is outside of our schudele range
      if (offsetCol < 0 || currentCol > state.lastCol) {
        playhead.style.left = "-900vw";
        return;
      }

      // setup css context
      wrapper.classList.add("tv-guide--has-playhead");
      if (
        wrapper.previousElementSibling.classList.contains("tv-guide__room-list")
      ) {
        wrapper.previousElementSibling.classList.add(
          "tv-guide__room-list--has-playhead"
        );
      }

      // position the playhead
      playhead.style.left = `${pxDist}px`;
    };

    const autoScrollToNow = () => {
      const firstCurrentOrNext = state.scheduleItems
        .filter((item) => item.dayOfYear === day)
        .find((scheduleItem) => isAfter(scheduleItem.endDateTime, new Date()));

      if (!!firstCurrentOrNext) {
        const item = wrapper.querySelector(
          `[data-guid="${firstCurrentOrNext.guid}"]`
        );
        let left = item.offsetLeft;
        const roomLink = document.querySelector(".schedule-room__link");
        if (roomLink) left = left - roomLink.clientWidth;

        wrapper.scrollTo({
          top: 0,
          left,
          behavior: "smooth",
        });
      }
    };

    // Render
    // * TimeScale
    wrapper.appendChild(TVGuideTimeScale());

    // * Playhead
    if (isToday(day)) {
      wrapper.innerHTML += PlayHead();
    }

    // single channel
    if (!rooms) {
      wrapper.appendChild(DaySchedule({ day, mode }));
    }

    // multi-channel
    if (rooms) {
      // * Current Channel
      if (rooms.some((room) => room.isCurrent)) {
        wrapper.appendChild(
          DaySchedule({ day, room: rooms.find((room) => room.isCurrent), mode })
        );
      }

      // * Other Channels
      rooms
        .filter((room) => !room.isCurrent)
        .forEach((room) =>
          wrapper.appendChild(DaySchedule({ day, room, mode }))
        );
    }

    // After Render
    requestAnimationFrame(() => {
      sizeRoomLinks();
      detectOverflowingItems();

      if (isToday(day)) {
        positionPlayhead();
        autoScrollToNow();
        setInterval(() => {
          positionPlayhead();
          autoScrollToNow();
        }, 60000);
      }
    });

    // Listeners
    window.addEventListener("resize", debounce(sizeRoomLinks, 100));

    return wrapper;
  }

  function PlayHead() {
    return `
      <figure class="tv-guide__playhead">
        <svg class="tv-guide__playhead-triangle" width="13" height="12" viewBox="0 0 13 12" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6.5 12L0.00480941 0.75L12.9952 0.750001L6.5 12Z" fill="currentColor"/>
        </svg>
        <figure class="tv-guide__playhead-bar"></figure>
      </figure>
    `;
  }

  function RoomContainer({ day, room, mode }) {
    const wrapper = document.createElement("div");
    wrapper.classList.add("schedule-room");

    if (room.isCurrent) wrapper.classList.add("schedule-room--current");

    wrapper.innerHTML = `
      ${
        mode === "scroll"
          ? `
        <div id="slider-nav-${room.id}" class="schedule-nav">
          <a class="slider-button prev-arrow">${WP.schedule.leftArrow}</a>
          <a class="slider-button next-arrow">${WP.schedule.rightArrow}</a>
        </div>
      `
          : ""
      }
      <div class="schedule-room__row">
        ${RoomLink({ room })}
      </div>
    `;
    wrapper
      .querySelector(".schedule-room__row")
      .appendChild(DaySchedule({ day, room, mode }));
    return wrapper;
  }

  function RoomLink({ room }) {
    return `
      <a href="${room.url}" class="schedule-room__link">
        ${room.icon ? room.icon : ""}
        <span class="schedule-room__title">${room.title}</span>
        ${
          room.isCurrent
            ? `<span class="schedule-room__current-indicator">You are here</span>`
            : ""
        }
      </a>
    `;
  }

  function DaySchedule({ day = null, room = null, mode = null }) {
    const wrapper = document.createElement("div");
    wrapper.classList.add(
      "schedule__day",
      `schedule__day--${
        WP.schedule.detailsMode === "feature" ? "featured" : mode
      }`
    );

    // tv-guide style requires some styling in js (configurable in admin)
    if (mode === "grid") {
      wrapper.style.gridTemplateColumns = `repeat(96, [grid-line] ${WP.schedule.blockSize}px)`;
    }

    if (day) {
      // for multi-day paginated schedule
      if (room) {
        wrapper.dataset.room = room.id;
        state.scheduleItems
          .filter((item) => !!item.roomId)
          .filter((item) => item.dayOfYear === day && item.roomId === room.id)
          .forEach((item) =>
            wrapper.appendChild(ScheduleItem({ ...item, mode }))
          );
      } else {
        state.scheduleItems
          .filter((item) => item.dayOfYear === day)
          .forEach((item) =>
            wrapper.appendChild(ScheduleItem({ ...item, mode }))
          );
      }
    } else {
      // for consolidated schedule
      if (room) {
        wrapper.dataset.room = room.id;
        state.scheduleItems
          .filter((item) => !!item.roomId)
          .filter((item) => item.roomId === room.id)
          .forEach((item) =>
            wrapper.appendChild(ScheduleItem({ ...item, mode }))
          );
      } else {
        state.scheduleItems.forEach((item) =>
          wrapper.appendChild(ScheduleItem({ ...item, mode }))
        );
      }
    }

    return wrapper;
  }

  function ScheduleItem({
    guid,
    title,
    startDateTime,
    endDateTime,
    calLink,
    presenters,
    tags,
    excerpt,
    mode,
    image,
  }) {
    // dom el
    const wrapper = document.createElement("div");
    wrapper.classList.add("schedule-item", "card");
    wrapper.dataset.guid = guid;

    // enhance with some data-attrs  / classes depending on mode
    if (WP.schedule.detailsMode === "feature" && guid === state.activeItem)
      wrapper.classList.add("feature-active");

    // enhance with some data-attrs for tv-guide style schedule
    if (mode === "grid") {
      const startColumn = placeOnGrid(startDateTime),
        span = Math.round(differenceInMinutes(endDateTime, startDateTime) / 15);
      wrapper.dataset.start = startColumn - state.gridOffset;
      wrapper.dataset.span = span;
    }

    // methods
    function dynamicCountdownOrLiveBadge() {
      const countdown = difference(startDateTime),
        liveNow = isLiveNow(startDateTime, endDateTime);

      if (countdown && differenceInDays(startDateTime, new Date()) < 6) {
        wrapper.querySelector(".countdown-wrap").style.display = "block";
        wrapper.querySelector(".countdown").textContent = countdown;
      } else {
        wrapper.querySelector(".countdown-wrap").style.display = "none";
      }

      if (liveNow) {
        wrapper.querySelector(".live-badge").style.display = "inline-flex";
      } else {
        wrapper.querySelector(".live-badge").style.display = "none";
      }

      if (!liveNow && !countdown) {
        clearTimeout(timerInterval);
      }
    }
    function handleClick() {
      if (WP.schedule.detailsMode === "feature") {
        runInAction(() => {
          state.activeItem = guid;
        });
      } else {
        popMega(guid);
      }
    }
    function addToCalPortal(e) {
      setTimeout(() => {
        const calLink = e.target,
          calDropdown = document.querySelector(".addeventatc-selected"),
          linkPos = calLink.getBoundingClientRect(),
          bodyPos = document.body.getBoundingClientRect();

        document.body.appendChild(calDropdown);
        calDropdown.style.top = `${linkPos.top - bodyPos.top}px`;
        calDropdown.style.left = `${linkPos.left - bodyPos.left}px`;
      }, 100);
    }

    // template
    let template = `
      <div class="schedule-item__top">
        ${
          WP.schedule.showThumbnails && image
            ? `<div class="schedule-item__thumbnail">${image}</div>`
            : ""
        }
        <div class="schedule-item__right">
          <div class="schedule-item__meta">
          <span class="schedule-item__time">
            ${formatTimespan(startDateTime, endDateTime)}
          </span>
          <span class="countdown-wrap" style="display:none">
            ${WP.schedule.timerIcon}
            <span>Starts in: </span>
            <span class="countdown"></span>
          </span>
          <span
            class="live-badge live-badge--small live-badge--noclick"
            style="display:none"
          >
            <span class="live-badge-content">
              <img class="now-playing-icon black" src="${
                WP.schedule.themeFolder
              }/assets/img/anim-playing-black.gif">
              <img class="now-playing-icon white" src="${
                WP.schedule.themeFolder
              }/assets/img/anim-playing-white.gif">
              <span>LIVE NOW</span>
            </span>
          </span>
          ${calLink}
        </div>
        <span class="schedule-item__title">${title}</span>
    `;
    if (tags.length) {
      template += '<div class="content-tags schedule-item__tags">';
      template += tags
        .map(
          (tag) =>
            `<span class="content-tag content-tag-${tag.slug} schedule-item__tag" style="background:${tag.color}">${tag.label}</span>`
        )
        .join("");
      template += "</div>";
    }
    if (presenters.length) {
      template += '<div class="schedule-item__presenters">';
      template += presenters
        .map((presenter) => {
          const errHandler = `onerror="this.style.display='none';"`;
          presenter.avatar = presenter.avatar.replace(
            "alt=",
            `${errHandler} alt=`
          );
          return `<span class="schedule-item__presenter">${presenter.avatar} ${presenter.name}</span>`;
        })
        .join("");
      template += "</div>";
    }
    template += `
      </div>
    </div>
    <div class="schedule-item__excerpt">${excerpt}</div>`;
    wrapper.innerHTML = template;

    // register dynamic events/listeners
    const timerInterval = setInterval(dynamicCountdownOrLiveBadge, 1000);
    dynamicCountdownOrLiveBadge();
    wrapper.addEventListener("click", (e) => {
      if (e.target.classList.contains("atc_node")) return;
      handleClick();
    });
    wrapper
      .querySelector(".addeventatc")
      .addEventListener("click", addToCalPortal);

    // output
    return wrapper;
  }

  // helpers

  // Convert DateTime objects to a grid position on a 15-minute column grid
  // ? used in tv-guide style schedules
  function placeOnGrid(dateTimeObject) {
    const floatTime =
        dateTimeObject.getHours() + dateTimeObject.getMinutes() / 60,
      roundedFloatTime = (Math.round(floatTime * 4) / 4).toFixed(2),
      columnNumber = roundedFloatTime / 0.25 + 1;
    return columnNumber;
  }

  // Use "slider arrows" to control native horizontal scroll on tv-guide style schedules
  function scrollTVGuide(dir, distance) {
    if (state.mode !== "grid") return;

    const scrollContainer = document.querySelector(".tv-guide"),
      currentPos = scrollContainer.scrollLeft,
      futurePos =
        dir === "right" ? currentPos + distance : currentPos - distance;

    scrollContainer.scrollTo({
      top: 0,
      left: futurePos,
      behavior: "smooth",
    });
  }
  if (WP.schedule.horizontalMode === "grid") {
    document
      .querySelector(".event-section-schedule .prev-arrow")
      .addEventListener("click", () => {
        scrollTVGuide("left", 300);
      });
    document
      .querySelector(".event-section-schedule .next-arrow")
      .addEventListener("click", () => {
        scrollTVGuide("right", 300);
      });
  }

  // Init slider on dynamically created schedules and fire autoschedule
  function setSliderState(mode, rooms) {
    setTimeout(() => {
      if (mode === "scroll") {
        // clone & replace arrows (glider destroy event breaks them)
        let oldSliderArrows = $(".schedule-nav-arrows");
        let newSliderArrows = oldSliderArrows.clone();
        oldSliderArrows.replaceWith(newSliderArrows);

        const sliderConfig = {
          slidesToShow: "auto",
          slidesToScroll: "auto",
          rewind: false,
          duration: 1,
        };

        // single room
        if (!rooms) {
          new Glider(document.querySelector(".schedule__day"), {
            ...sliderConfig,
            itemWidth: 386,
            exactWidth: true,
            arrows: {
              prev: document.querySelector(".schedule-nav .prev-arrow"),
              next: document.querySelector(".schedule-nav .next-arrow"),
            },
          });
          $(".schedule__day").each(function () {
            // TODO: A better, global way to handle this
            // copy of checkEndcap code from `event-sliders.js` code`
            const lastItem = $(this).find(".card:last");
            if (lastItem.length) {
              const lastItemOffset = lastItem.offset();
              const lastItemEdge =
                lastItemOffset.left + lastItem.outerWidth() + 8;
              const listBounds = $(this).outerWidth();
              const clipsSection = $(this).closest(".event-section-content");

              if (lastItemEdge < listBounds) {
                clipsSection.addClass("no-endcap");
              } else {
                clipsSection.removeClass("no-endcap");
              }
            }
          });
        }

        // multiroom
        else {
          rooms.forEach((room) => {
            new Glider(
              document.querySelector(`.schedule__day[data-room="${room.id}"]`),
              {
                ...sliderConfig,
                itemWidth: 386,
                exactWidth: true,
                arrows: {
                  prev: document.querySelector(
                    `#slider-nav-${room.id} .prev-arrow`
                  ),
                  next: document.querySelector(
                    `#slider-nav-${room.id} .next-arrow`
                  ),
                },
              }
            );
          });
        }

        autoSchedule();
        setInterval(autoSchedule, 60000);
      }
    }, 0);
  }

  // Mode breakpoint
  function modeMediaQuery() {
    runInAction(() => {
      if (window.matchMedia("(max-width: 640px)").matches) {
        state.mode = "list";
      } else {
        state.mode = state.userChosenMode || initialMode;
      }
    });
  }

  // Convert unix timestamp to date object
  function getDateFromPHPUnix(unixTimestamp) {
    return new Date(unixTimestamp * 1000);
  }

  // Format schedule item duration as string
  function formatTimespan(startDate, endDate) {
    let startDay = isToday(startDate) ? "Today" : format(startDate, "iii"),
      endDay = isToday(endDate) ? "Today" : format(endDate, "iii");
    const startTime = format(startDate, "h:mmaaa"),
      endTime = format(endDate, "h:mmaaa");

    if (
      isBefore(startDate, new Date()) ||
      differenceInDays(startDate, new Date()) >= 6
    ) {
      startDay = format(startDate, "iii, MMM do,");
      endDay = format(endDate, "iii, MMM do,");
    }
    return `<span class="schedule-item__start-day">${startDay}</span> <span class="schedule-item__start-time">${startTime}</span><span class="schedule-item__hyphen-separator"> - </span>${!isSameDay(endDate, startDate) ? `<span class="schedule-item__end-day">${endDay}</span>` : ""} <span class="schedule-item__end-time">${endTime}</span>`;
  }

  // Format schedule item duration as two-line long form string
  function formatTimespanLongform(startDate, endDate) {
    const day = isToday(startDate)
        ? '<span class="schedule-modal__weekday">Today</span>'
        : `<span class="schedule-modal__weekday">${format(
            startDate,
            "iiii"
          )}</span><span class="schedule-modal__comma-separator">, </span><span class="schedule-modal__date">${format(
            startDate,
            "MMMM do"
          )}</span><span class="schedule-mdoal__comma-separator">, </span><span class="schedule-modal__year">${format(
            startDate,
            "yyyy"
          )}</span>`,
      startTime = format(startDate, "h:mmaaa"),
      endTime = format(endDate, "h:mmaaa");
    return `${day} <span class="schedule-modal__line-break"><br></span><span class="schedule-modal__start-time">${startTime}</span><span class="schedule-modal__hyphen-separator"> - </span><span class="schedule-modal__end-time">${endTime}</span>`;
  }

  // Simple string padding for hrs, mins, and seconds
  function simplePad(int) {
    if (int.toString().length < 2) return `0${int}`;
    else return int.toString();
  }

  // Countdown timers
  function difference(startDateTime) {
    const now = new Date(),
      secondsPast = differenceInSeconds(startDateTime, now),
      h = differenceInHours(startDateTime, now);

    if (secondsPast.toString().includes("-")) {
      return null;
    }

    if (h === 0) {
      // less than an hour
      const m = differenceInMinutes(startDateTime, now),
        ss = simplePad(secondsPast % 60);
      return `${m}m, ${ss}s`;
    } else if (h <= 24) {
      // less than a day
      const mMod = differenceInMinutes(startDateTime, now) % 60;
      return `${h}h, ${mMod}m`;
    } else {
      const d = differenceInDays(startDateTime, now),
        hMod = h % (d * 24);
      return `${d}d, ${hMod}h`;
    }
  }

  // Live now?
  function isLiveNow(startDateTime, endDateTime) {
    const now = new Date(),
      startInPast = differenceInSeconds(startDateTime, now) < 0,
      endInFuture = differenceInSeconds(endDateTime, now) > 0;

    return startInPast && endInFuture;
  }

  // Auto positioning on sliders
  function autoSchedule() {
    if (state.mode !== "scroll") return;

    const now = Math.ceil(new Date().getTime() / 1000);

    if (state.rooms) {
      state.rooms.forEach((room) => {
        let goTo = 0;
        let glider = Glider(
          document.querySelector(`.schedule__day[data-room="${room.id}"]`)
        );
        $(`.schedule__day[data-room="${room.id}"] .schedule-item`).each(
          function (i) {
            const guid = $(this).data("guid");
            const endTimestamp =
              state.scheduleItems
                .find((item) => item.guid == guid)
                ?.endDateTime.getTime() / 1000;
            if (endTimestamp > now) {
              goTo = i;
              // we found the current or next event, break the loop
              return false;
            }
          }
        );
        glider.scrollItem(parseInt(goTo));
      });
    } else {
      let goTo = 0;
      let guid;
      let glider = Glider(document.querySelector(".schedule__day"));

      $(".schedule-item").each(function (i) {
        guid = $(this).data("guid");
        const endTimestamp =
          state.scheduleItems
            .find((item) => item.guid == guid)
            ?.endDateTime.getTime() / 1000;

        if (endTimestamp > now) {
          goTo = i;
          // we found the current or next event, break the loop
          return false;
        }
      });

      glider.scrollItem(parseInt(goTo));

      if (WP.schedule.detailsMode === "feature") {
        runInAction(() => {
          state.activeItem = guid;
        });
      }
    }
  }

  // Fade schedule
  function fadeOut(el) {
    el.style.opacity = 0;
  }
  function fadeIn(el) {
    el.style.opacity = 0;
    requestAnimationFrame(() => {
      el.style.opacity = 1;
    });
  }

  // Show selected session details
  if (WP.schedule.detailsMode === "feature") {
    reaction(
      () => state.activeItem,
      (nextGuid, prevGuid) => {
        const next = `[data-guid="${nextGuid}"]`,
          prev = `[data-guid="${prevGuid}"]`,
          nextItem = document.querySelector(`${next}.schedule-item`),
          prevItem = document.querySelector(`${prev}.schedule-item`),
          nextDetail = document.querySelector(
            `${next}.schedule-event-details--featured`
          ),
          prevDetail = document.querySelector(
            `${prev}.schedule-event-details--featured`
          );

        if (nextItem) nextItem.classList.add("feature-active");
        if (prevItem) prevItem.classList.remove("feature-active");
        fadeOut(prevDetail);
        setTimeout(() => {
          prevDetail.style.display = "none";
          nextDetail.style.opacity = 0;
          nextDetail.style.display = "flex";
          fadeIn(nextDetail);
        }, 150);
      }
    );
  }

  // Pop mega modal to event by guid
  function popMega(guid) {
    const mega = document.querySelector(".schedule-mega-modal");
    const targetIndex = state.scheduleItems.findIndex(
      (event) => event.guid === guid
    );
    mega.classList.add("schedule-mega-modal--gliding");

    // Scroll to item and refresh
    let megaGlider = Glider(
      document.querySelector(".schedule-mega-modal__slider")
    );
    megaGlider.scrollItem(targetIndex);
    megaGlider.refresh();

    mega.classList.add("schedule-mega-modal--untransitioned");
    setTimeout(() => {
      mega.classList.replace(
        "schedule-mega-modal--untransitioned",
        "schedule-mega-modal--transitioned"
      );
      mega.classList.remove("schedule-mega-modal--gliding");
    }, 0);
  }

  // Close mega modal
  function closeMega() {
    const mega = document.querySelector(".schedule-mega-modal");
    mega.classList.replace(
      "schedule-mega-modal--transitioned",
      "schedule-mega-modal--untransitioned"
    );
    setTimeout(() => {
      mega.classList.remove("schedule-mega-modal--untransitioned");
    }, 350);
  }
  const megaClose = document.querySelector(".schedule-mega-modal__close");
  if (megaClose) megaClose.addEventListener("click", closeMega);
  document.body.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      closeMega();
    }
  });
  $(".schedule-event-details__descbuttons .button.is-internal").on(
    "click",
    closeMega
  );

  // Localized event duration strings in mega modal
  function localizedDurationInMega() {
    Array.from(document.querySelectorAll(".schedule-event-details")).forEach(
      (event) => {
        const guid = event.dataset.guid;
        const { startDateTime, endDateTime } = state.scheduleItems.find(
          (event) => event.guid === guid
        );
        event.querySelector(".schedule-event-details__time").innerHTML =
          formatTimespanLongform(startDateTime, endDateTime);
      }
    );
  }
  localizedDurationInMega();

  // Dynamic mega modal content (countdown, live badge, calendar link)
  Array.from(document.querySelectorAll(".schedule-event-details")).forEach(
    (event) => {
      const modalTimer = setInterval(modalDynamics, 1000);

      function modalDynamics() {
        const guid = event.dataset.guid,
          { startDateTime, endDateTime } = state.scheduleItems.find(
            (event) => event.guid === guid
          ),
          countdown = difference(startDateTime),
          liveNow = isLiveNow(startDateTime, endDateTime);

        const roomButton = event.querySelector(".open-room-button");
        if (roomButton) {
          roomButton.style.display = liveNow ? "none" : "inline-flex";
        }

        if (countdown) {
          event.querySelector(".countdown-wrap").style.display = "block";
          event.querySelector(".countdown").textContent = countdown;
          event.querySelector(".addeventatc").style.display = "flex";
        } else {
          event.querySelector(".countdown-wrap").style.display = "none";
          event.querySelector(".addeventatc").style.display = "none";
        }

        // Check that "add to calendar" script has loaded successfully
        if (typeof addeventatc == "undefined") {
          // If there was an error, hide the "add to calendar" markup
          event.querySelector(".addeventatc").style.display = "none";
        }

        const watchButton = event.querySelector(".watch-live-button");
        if (watchButton) {
          watchButton.style.display = liveNow ? "inline-flex" : "none";
          watchButton.addEventListener("click", closeMega);
        }
        event.querySelector(".live-badge").style.display = liveNow
          ? "inline-flex"
          : "none";

        if (!liveNow && !countdown) {
          clearTimeout(modalTimer);
          event.querySelector(".finished-badge").style.display = "block";
        }
      }
    }
  );

  // Show timezone string if the user's TZ doesn't match the site timezone
  function conditionallyShowTimezone() {
    const userTime = new Date(),
      siteTime = new Date(WP.schedule.siteTime.date),
      showTimeZone = userTime.getHours() !== siteTime.getHours(),
      uiTarget = document.querySelector(".event-section-timezone");

    if (showTimeZone) {
      const userTimeZone = /.*\s(.+)/.exec(
        new Date().toLocaleDateString(navigator.language, {
          timeZoneName: "short",
        })
      )[1];
      uiTarget.innerHTML = `All times in ${userTimeZone}`;
    }
  }
  conditionallyShowTimezone();

  // Hot-swap live chapter information into live media caption
  if (WP.schedule.remoteControlCaptions) {
    maybeModifyLiveCaption();
    setInterval(maybeModifyLiveCaption, 1000);
  }

  function maybeModifyLiveCaption() {
    if (heroState.mode !== "live") return;

    const overrideLiveMediaCaption = (title) => {
      runInAction(() => {
        heroState.caption = title;
        heroState.captionOverridedBySchedule = true;
      });
    };
    const resetLiveMediaCaption = () => {
      runInAction(() => {
        heroState.caption = heroState.liveTitle;
        heroState.captionOverridedBySchedule = false;
      });
    };

    const scheduleItemsInThisRoom =
      state.rooms === null
        ? state.scheduleItems
        : state.scheduleItems.filter(
            (item) =>
              item.roomId ===
              state.rooms.find((room) => room.title === heroState.roomName).id
          );

    const liveScheduleItem = scheduleItemsInThisRoom.find(
      ({ startDateTime, endDateTime }) => isLiveNow(startDateTime, endDateTime)
    );

    // we have a live schedule item in this room, and we are currently watching the live media
    // --> hot swap the caption
    if (liveScheduleItem && liveTypes.includes(heroState.currentType)) {
      overrideLiveMediaCaption(liveScheduleItem.title);
    }

    // we don't have a live schedule item in this room, but the hero caption is "overridden"
    //  --> it must have just ended, reset the caption
    if (!liveScheduleItem && heroState.captionOverridedBySchedule) {
      resetLiveMediaCaption();
    }
  }

  // Set up Prev/Next buttons on featured sessions
  function handleFeaturedSessionNext(e) {
    let targetGuid;
    const type = e.target.closest("a").textContent.trim().toLowerCase();
    if (type === "previous") {
      targetGuid =
        document.querySelector(".feature-active")?.previousElementSibling
          ?.dataset.guid;
    }
    if (type === "next") {
      targetGuid =
        document.querySelector(".feature-active")?.nextElementSibling?.dataset
          .guid;
    }
    if (targetGuid) runInAction(() => (state.activeItem = targetGuid));
  }
  document
    .querySelectorAll(".schedule-event-details--featured [class*='-arrow']")
    ?.forEach((el) => el.addEventListener("click", handleFeaturedSessionNext));
});
